// @ts-nocheck
import { DatePickerPresentationProps } from "@insite/mobius/DatePicker";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries, { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { boxStyles } from "@store/Themes/common/mixins/box";
import { baseGridProps, BaseGridProps } from "@store/Themes/common/props/baseGrid";
import { checkoutOrderTotalItemStyles } from "@store/Themes/common/mixins/checkbox";
import { editLinkStyles } from "@store/Themes/common/mixins/link";
import { contentSpacingStyles } from "@store/Themes/common/mixins/spacing";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { clearfixStyles, iconScalableStyles } from "@toolkit/Themes/common/mixins";
import { css } from "styled-components";

// -- GRID

export interface StoreCheckoutGridProps extends BaseGridProps {
    sideColumnMobile?: GridItemProps;
    sideColumnDesktop?: GridItemProps;
}

const sideColumnProps: GridItemProps = {
    width: [12, 12, 5, 3, 3],
    printWidth: 12,
};

export const checkoutGridProps: StoreCheckoutGridProps = {
    container: {
        ...baseGridProps.container,
        offsetCss: css`
            justify-content: space-between;
        `,
    },
    mainColumn: {
        width: [12, 12, 7, 7, 7],
        printWidth: 12,
    },
    sideColumn: sideColumnProps,
    sideColumnMobile: {
        ...sideColumnProps,
        css: css`
            ${sideColumnProps?.css}
            display: block;
            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    sideColumnDesktop: {
        ...sideColumnProps,
        css: css`
            ${sideColumnProps?.css}
            display: none;
            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        null,
                        css`
                            display: block;
                        `,
                    ],
                    mediaOptions.min,
                )}

            @media print {
                display: none !important;
            }
        `,
    },
};

// -- TITLES

export interface StoreCheckoutTitleProps {
    wrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    buttonsWrapper?: InjectableCss;
}

export const checkoutTitleProps: StoreCheckoutTitleProps = {
    wrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            border-bottom: ${pxToRem(3)} solid ${getColor("common.border")};
            ${breakpointValues([1, null, 1.25, null, 1.5], "padding-bottom")}
        `,
    },
    heading: {
        variant: "h1",
        forwardAs: "h1",
        css: css`
            margin-bottom: 0;
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            margin-left: auto;
        `,
    },
};

export interface StoreCheckoutTitleWithLinksProps {
    heading?: TypographyPresentationProps;
    container?: InjectableCss;
    wrapper?: InjectableCss;
    wrapperItem?: InjectableCss;
    item?: InjectableCss;
    printLink?: LinkPresentationProps;
    backLink?: LinkPresentationProps;
}

const getCheckoutTitleLinkProps = (iconSrc?: string) => {
    const props: LinkPresentationProps = {};

    if (iconSrc) {
        props.icon = {
            iconProps: {
                src: iconSrc,
                css: css`
                    ${iconScalableStyles()}
                    font-size: 1.5em;
                    margin-right: 0.3em;
                    transition: inherit !important;
                `,
            },
        };
    }

    props.css = css`
        ${editLinkStyles()}
        display: block;
    `;

    return props;
};

export const checkoutTitleWithLinksProps: StoreCheckoutTitleWithLinksProps = {
    heading: checkoutTitleProps.heading,
    container: {
        css: css`
            ${checkoutTitleProps.wrapper?.css}
            ${clearfixStyles()}
            display: block;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            flex-flow: row wrap;
            margin: 0.5em 0 0 -1.5em;
        `,
    },
    wrapperItem: {
        css: css`
            display: flex;
            margin: 0.5em 0 0 1.5em;
        `,
    },
    item: {
        css: css`
            display: flex;
            align-items: center;

            &::before {
                content: "";
                position: relative;
                top: 0.1em;
                display: block;
                width: ${pxToRem(1)};
                height: 1em;
                margin: 0 0.5em;
                background-color: ${getColor("common.border")};
            }

            &:first-child::before {
                display: none;
            }
        `,
    },
    backLink: getCheckoutTitleLinkProps("ArrowLeft"),
    printLink: getCheckoutTitleLinkProps("Printer"),
};

export const checkoutSubtitleProps: TypographyPresentationProps = {
    variant: "h4",
    forwardAs: "h3",
    css: css`
        margin-bottom: ${({ theme }) => theme.design.typography.spacing.base};
    `,
};

export const checkoutShippingBillingSubtitle: TypographyProps = {
    variant: "h6",
    as: "h4",
    css: css`
        @media print {
            font-size: ${pxToRem(12)};
        }
        margin-bottom: ${pxToRem(5)};
    `,
};

// ORDER TOTAL

export interface StoreOrderTotalStyles {
    displayWrapper?: InjectableCss;
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    title?: TypographyProps;
    titleGridItem?: GridItemProps;
    value?: TypographyProps;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    subtitle?: TypographyProps;
    // Store Custom Code
    label?: TypographyProps;
}

const orderTotalValueProps = {
    css: css`
        margin-left: auto;
        font-size: ${({ theme }) => theme.design.typography.size.base};
    `,
};

export const orderTotalProps: StoreOrderTotalStyles = {
    displayWrapper: {
        css: css`
            padding: 1.875em 1.25em;
            background-color: ${getColor("common.accent")};
            // Bunzl Custom Code
            border-radius: ${({ theme }) => theme.design.radius.medium};
            @media print {
                border-bottom: none;
                padding-bottom: 0;
                max-width: 50%;
            }
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            min-height: ${pxToRem(300)};
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    container: {
        gap: 0,
    },
    title: {
        as: "h2",
        variant: "h4",
    },
    titleGridItem: {
        width: 12,
        css: css`
            /* Bunzl Deleted Code Child selector and padding*/
            font-weight: ${({ theme }) => theme.design.typography.weight.black};
            /* Bunzl Custom Code */
            padding-bottom: ${({ theme }) => theme.design.layout.spacing.smaller};
        `,
    },
    labelGridItem: {
        width: 7,
        css: css`
            flex-direction: column;
            justify-content: center;
            ${checkoutOrderTotalItemStyles()}
        `,
    },
    valueGridItem: {
        width: 5,
        css: css`
            align-items: center;
            justify-content: flex-end;
            ${checkoutOrderTotalItemStyles()}
        `,
    },
    value: orderTotalValueProps,
    /* Bunzl Custom Code Start */
    totalLabel: {
        color: "text.main",
        variant: "h5",
        css: css`
            font-weight: ${({ theme }) => theme.design.typography.weight.bold};
            size: ${({ theme }) => theme.design.typography.size.smaller};
        `,
    },
    totalValue: {
        color: "text.main",
        variant: "h5",
    },
    subtitle: {
        italic: true,
        color: "text.main",
        css: css`
            margin-top: ${pxToRem(4)}
            font-size: ${({ theme }) => theme.design.typography.size.base};
            line-height: ${({ theme }) => theme.design.typography.lineHeight.smaller};
            font-weight: ${({ theme }) => theme.design.typography.weight.bold};
        `,
    },
    label: {
        color: "text.main",
        css: css`
            font-size: ${({ theme }) => theme.design.typography.size.small};
            line-height: ${({ theme }) => theme.design.typography.lineHeight.smaller};
            font-weight: ${({ theme }) => theme.design.typography.weight.base};
        `,
        /* Bunzl Custom Code End */
    },
};

interface CheckoutInputGridItemStyle {
    gridItem: GridItemProps;
    datePickerStyle: DatePickerPresentationProps;
}

export const checkoutInputGridItemStyle: CheckoutInputGridItemStyle = {
    gridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            > * {
                width: 100%;
            }
        `,
    },
    datePickerStyle: {
        cssOverrides: {
            formInputWrapper: css`
                width: 100%;
            `,
        },
    },
};

export const checkoutFulfillmentMethodWrapperProps: InjectableCss = {
    css: css`
        ${boxStyles()}
        ${contentSpacingStyles(0, 0, pxToRem(16), 0)}
        /* Bunzl Custom Code Start */
        background-color: ${getColor("common.shadesLight")};
        border-radius: ${({ theme }) => theme.design.radius.medium};
        border: 0;
        box-shadow: none;
        /* Bunzl Custom Code End */
    `,
};
